import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Tab,
  Tabs,
  Collapse,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import api from "../api/api";
import moment from "moment";
import "../assets/styling/Styles.css";
import "./FieldHistorySeason.css";

const FieldHistory = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [fieldData, setFieldData] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [weeklyData, setWeeklyData] = useState([]);
  const [loadingCalculations, setLoadingCalculations] = useState(false);
  const [isListOpen, setIsListOpen] = useState(true);

  useEffect(() => {
    fetchSeasonHistory();
  }, [uid]);

  const fetchMissingNames = async (season) => {
    if (!season?.crops || season.crops.length === 0) return season;

    const updatedSeason = { ...season };

    if (season.source_crop_name === "Unknown" && season.crops[0]?.croptype_id) {
      try {
        const cropResponse = await api.get(
          `/crops/name/${season.crops[0].croptype_id}`
        );
        if (cropResponse.data.type === "success") {
          updatedSeason.source_crop_name = cropResponse.data.name;
        }
      } catch (error) {
        console.error("Error fetching crop name:", error);
      }
    }

    if (
      (season.source_variety_name === "Unknown" ||
        !season.source_variety_name) &&
      season.crops?.[0]?.crop_variety_id
    ) {
      try {
        const varietyResponse = await api.get(
          `/varieties/name/${season.crops[0].crop_variety_id}`
        );
        if (varietyResponse.data.type === "success") {
          updatedSeason.source_variety_name = varietyResponse.data.name;
        }
      } catch (error) {
        console.error("Error fetching variety name:", error);
      }
    }

    return updatedSeason;
  };

const fetchSeasonHistory = async () => {
  setLoading(true);
  try {
    const response = await api.get(`/fields/${uid}/seasons/history`);
    if (response.data.type === "success") {
      setFieldData(response.data.field);

      const seasonsWithNames = await Promise.all(
        response.data.seasons.map(async (season) => {
          const updatedSeason = await fetchMissingNames(season);
          
          if (season.crops && season.crops.length > 0) {
            const crop = season.crops[0];
            updatedSeason.season_start = crop.growingDate;
            updatedSeason.season_end = crop.growingDate 
                                      ? moment(crop.growingDate).add(crop.days, "days").format("YYYY-MM-DD")
                                      : null;
             if (crop.growingDate && crop.days) {
              const start = moment(crop.growingDate);
              const end = moment(crop.growingDate).add(crop.days, "days");
              updatedSeason.is_active = moment().isBetween(start, end, undefined, "[]"); // [] inclus start et end
            } else {
              updatedSeason.is_active = false;
            }
          } else {
            updatedSeason.is_active = false;
          }

          return updatedSeason;
        })
      );


      // Tri personnalisé : saison active en premier, puis par date décroissante
      const sortedSeasons = seasonsWithNames.sort((a, b) => {
        // Les saisons actives en premier
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        
        // Si les deux ont le même statut, trier par date
        const dateA = a.season_start ? new Date(a.season_start) : new Date(0);
        const dateB = b.season_start ? new Date(b.season_start) : new Date(0);
        return dateB - dateA;
      });

      setSeasons(sortedSeasons);
      if (sortedSeasons.length > 0) {
        setSelectedSeason(sortedSeasons[0]);
        fetchSeasonCalculations(sortedSeasons[0].crops?.[0]?.uid);
      }
    }
  } catch (error) {
    console.error("Error fetching season history:", error);
  } finally {
    setLoading(false);
  }
};

  const fetchSeasonCalculations = async (cropId) => {
    if (!cropId) return;

    setLoadingCalculations(true);
    try {
      const response = await api.get(`/calcul/season-calcul/${cropId}`);
      if (response.data.type === "success") {
        setWeeklyData(response.data.weeks || []);
      }
    } catch (error) {
      console.error("Error fetching season calculations:", error);
      setWeeklyData([]);
    } finally {
      setLoadingCalculations(false);
    }
  };

  const safeToFixed = (value, decimals = 2) => {
    if (value === null || value === undefined) return "0.00";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? "0.00" : num.toFixed(decimals);
  };

  const safeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  const handleSeasonClick = (season) => {
    setSelectedSeason(season);
    setActiveTab("overview");
      if (season.crops?.length > 0) {
    const firstCropId = season.crops[0].uid;
    fetchSeasonCalculations(firstCropId);
  }
    // Close list on mobile after selection
    if (window.innerWidth < 992) {
      setIsListOpen(false);
    }
  };

  const formatSeasonName = (season) => {
    if (season.season_name && season.season_name !== "Season 2025") {
      return season.season_name;
    }
    return moment(season.growingDate).format("MMM YYYY");
  };

  const getSeasonDuration = (season) => {
    const start = moment(season.season_start);
    let end;
  
  if (season.crops && season.crops.length > 0 && season.crops[0].days) {
    const crop = season.crops[0];
    const growingDate = crop.growingDate || crop.plantingDate || season.season_start;
    end = moment(growingDate).add(crop.days, 'days');
  } else if (season.season_end && moment(season.season_end).isValid()) {
    end = moment(season.season_end);
  } else {
    // Si la saison est active, utiliser la date actuelle
    end = season.is_active ? moment() : moment(season.season_start);
  }
    return end.diff(start, "days");
  };

  const getAllCropDetails = (season) => {
    if (!season?.crops || season.crops.length === 0) return [];

    return season.crops.map((crop, index) => {
      return {
        id: crop.id,
        name:
          season.source_crop_name !== "Unknown"
            ? season.source_crop_name
            : `Crop ${crop.croptype_id}`,
        variety:
          season.source_variety_name !== "Unknown"
            ? season.source_variety_name
            : "No variety",
        plantingDate: crop.plantingDate || crop.growingDate,
        surface: crop.surface,
        days: crop.days,
        rootDepth: crop.rootDepth,
        init: safeNumber(crop.init),
        dev: safeNumber(crop.dev),
        mid: safeNumber(crop.mid),
        late: safeNumber(crop.late),
        kc_init: safeNumber(crop.kc_init),
        kc_mid: safeNumber(crop.kc_mid),
        kc_late: safeNumber(crop.kc_late),
        index: index + 1,
        isKcModified: crop.is_kc_modified === 1,
      };
    });
  };

  const getWeekAverages = (dailyCalculations) => {
    if (!dailyCalculations || dailyCalculations.length === 0) {
      return { avgET0: 0, avgKc: 0, avgEtc: 0, lastBilan: 0 };
    }

    const avgET0 =
      dailyCalculations.reduce((sum, day) => sum + (day.ET0 || 0), 0) /
      dailyCalculations.length;
    const avgKc =
      dailyCalculations.reduce((sum, day) => sum + (day.kc || 0), 0) /
      dailyCalculations.length;
    const avgEtc =
      dailyCalculations.reduce((sum, day) => sum + (day.Etc || 0), 0) /
      dailyCalculations.length;

    const lastDay = dailyCalculations[dailyCalculations.length - 1];
    const lastBilan = lastDay?.bilan?.[lastDay.bilan.length - 1]?.value || 0;

    return { avgET0, avgKc, avgEtc, lastBilan };
  };

  const hasWeekIrrigation = (dailyCalculations) => {
    if (!dailyCalculations || dailyCalculations.length === 0) return false;
    return dailyCalculations.some((day) => day.irrigation_count > 0);
  };

  if (loading) {
    return (
      <Container
        fluid
        className="season-history-page px-2 px-md-4 py-3 py-md-5"
      >
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <div
            className="spinner-border text-info"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">{t("loading_season_data")}</p>
        </div>
      </Container>
    );
  }

  const cropDetails = selectedSeason ? getAllCropDetails(selectedSeason) : [];

  return (
    <Container
      fluid
      className="season-history-page px-2 px-md-4 py-3 py-md-4"
    >
      {/* Header */}
      <Row className="mb-3 mb-md-4">
        <Col>
          <div className="season-history-hero d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-2 gap-md-3 w-100 w-md-auto">
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/Fields/${uid}`)}
                style={{
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  padding: 0,
                  border: "2px solid var(--history-border, #e1eaec)",
                  flexShrink: 0,
                }}
                className="season-history-back d-flex align-items-center justify-content-center"
              >
                <i className="fas fa-arrow-left" aria-hidden="true"></i>
              </Button>
              <div className="season-history-heading flex-grow-1">
                <h2
                  className="mb-1"
                  style={{
                    fontWeight: "700",
                    color: "var(--history-teal, #0798ad)",
                    fontSize: "clamp(1.25rem, 5vw, 1.75rem)",
                  }}
                >
                  {t("season_history")}
                </h2>
                <p
                  className="mb-0 text-muted"
                  style={{ fontSize: "clamp(0.8rem, 3vw, 1rem)" }}
                >
                  {fieldData?.name} - {t("all_seasons")}
                </p>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        {/* Seasons List with Toggle */}
        <Col lg={12} md={12} className="mb-3 mb-lg-4">
          <Card className="season-history-list-card shadow-sm">
            <Card.Header
              style={{
                background: "linear-gradient(135deg, var(--history-teal, #0798ad) 0%, var(--history-teal-soft, #5fc9d3) 100%)",
                color: "white",
                padding: "12px 16px",
                fontSize: "clamp(0.9rem, 3vw, 1rem)",
                fontWeight: "600",
                cursor: "pointer",
              }}
              onClick={() => setIsListOpen(!isListOpen)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsListOpen(!isListOpen);
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={isListOpen}
              className="season-history-list-header d-flex justify-content-between align-items-center"
            >
              <span>
                <i className="fas fa-calendar-alt me-2" aria-hidden="true"></i>
                {t("seasons_list")} ({seasons.length})
              </span>
              <i
                className={`fas fa-chevron-${isListOpen ? "up" : "down"}`}
                style={{ transition: "transform 0.3s ease" }}
                aria-hidden="true"
              ></i>
            </Card.Header>
            <Collapse in={isListOpen}>
              <Card.Body
                className="season-history-list-body p-0"
                style={{ maxHeight: "60vh", overflowY: "auto" }}
              >
                {seasons.length === 0 ? (
                  <div className="text-center py-4 py-md-5 text-muted px-3">
                    <i className="fas fa-inbox fa-2x fa-md-3x mb-3 opacity-50" aria-hidden="true"></i>
                    <p style={{ fontSize: "clamp(0.85rem, 3vw, 1rem)" }}>
                      {t("no_seasons_found")}
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {seasons.map((season) => {
                      const isCurrent = selectedSeason?.id === season.id;
                      const cropInfo = season.crops?.[0]
                        ? {
                            name:
                              season.source_crop_name &&
                              season.source_crop_name !== "Unknown"
                                ? season.source_crop_name
                                : `Crop ${season.crops[0].croptype_id}`,
                            variety:
                              season.source_variety_name &&
                              season.source_variety_name !== "Unknown"
                                ? season.source_variety_name
                                : "",
                          }
                        : null;

                      return (
                        <div
                          key={season.id}
                          onClick={() => handleSeasonClick(season)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleSeasonClick(season);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isCurrent}
                          className={`list-group-item list-group-item-action ${
                            isCurrent ? "active" : ""
                          }`}
                          style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            backgroundColor: isCurrent ? "var(--history-teal, #0798ad)" : "var(--sf-night-surface, white)",
                            color: isCurrent ? "white" : "inherit",
                            borderLeft: isCurrent
                              ? "4px solid var(--history-teal, #0798ad)"
                              : "4px solid transparent",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <div className="d-flex flex-wrap align-items-center mb-2 gap-2">
                                <h6
                                  className="mb-0"
                                  style={{
                                    fontWeight: "600",
                                    color: isCurrent ? "white" : "var(--history-deep, #063f4b)",
                                    fontSize: "clamp(0.9rem, 3vw, 1rem)",
                                  }}
                                >
                                  {formatSeasonName(season)}
                                </h6>
                                <Badge
                                  bg={
                                    season.is_active
                                      ? isCurrent
                                        ? "light"
                                        : "success"
                                      : isCurrent
                                      ? "secondary"
                                      : "outline-secondary"
                                  }
                                  text={
                                    season.is_active
                                      ? isCurrent
                                        ? "dark"
                                        : "white"
                                      : isCurrent
                                      ? "white"
                                      : "dark"
                                  }
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {season.is_active
                                    ? t("active")
                                    : t("completed")}
                                </Badge>
                              </div>
                              <p
                                className="mb-2"
                                style={{
                                  fontSize: "clamp(0.75rem, 2.5vw, 0.85rem)",
                                  color: isCurrent
                                    ? "rgba(255,255,255,0.9)"
                                    : "var(--history-muted, #71848a)",
                                }}
                              >
                                {moment(season.season_start).format(
                                  "MMM DD, YYYY"
                                )}
                                {season.season_end
                                  ? ` - ${moment(season.season_end).format(
                                      "MMM DD, YYYY"
                                    )}`
                                  : ` - ${t("present")}`}
                              </p>
                              {cropInfo && (
                                <div
                                  style={{
                                    fontSize: "clamp(0.7rem, 2.5vw, 0.8rem)",
                                  }}
                                >
                                  <span
                                    style={{
                                      color: isCurrent
                                        ? "rgba(255,255,255,0.95)"
                                        : "var(--sf-night-success, #28a745)",
                                    }}
                                  >
                                    <i className="fas fa-seedling me-1" aria-hidden="true"></i>
                                    {cropInfo.name}
                                  </span>
                                  {cropInfo.variety && (
                                    <span
                                      className="ms-2"
                                      style={{
                                        color: isCurrent
                                          ? "rgba(255,255,255,0.8)"
                                          : "var(--history-muted, #71848a)",
                                      }}
                                    >
                                      ({cropInfo.variety})
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {isCurrent && (
                              <i
                                className="fas fa-check-circle ms-2"
                                style={{ color: "white", fontSize: "1.2rem" }}
                                aria-hidden="true"
                              ></i>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Collapse>
          </Card>

          {/* Mobile: Show selected season info when list is collapsed */}
          {!isListOpen && selectedSeason && (
            <Card className="season-history-selected-card shadow-sm mt-3 d-lg-none">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted d-block mb-1">
                      {t("selected_season")}
                    </small>
                    <h6 className="mb-0" style={{ color: "var(--history-teal, #0798ad)" }}>
                      {formatSeasonName(selectedSeason)}
                    </h6>
                  </div>
                  <Badge
                    bg={selectedSeason.is_active ? "success" : "secondary"}
                  >
                    {selectedSeason.is_active ? t("active") : t("completed")}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
      <Row>
        {/* Season Details */}
        <Col lg={12} md={12}>
          {!selectedSeason ? (
            <Card className="season-history-empty-card shadow-sm" style={{ minHeight: "60vh" }}>
              <Card.Body className="d-flex align-items-center justify-content-center">
                <div className="text-center text-muted px-3">
                  <i className="fas fa-hand-pointer fa-2x fa-md-3x mb-3 opacity-50" aria-hidden="true"></i>
                  <h5 style={{ fontSize: "clamp(1rem, 4vw, 1.25rem)" }}>
                    {t("select_season_to_view")}
                  </h5>
                  <p style={{ fontSize: "clamp(0.85rem, 3vw, 1rem)" }}>
                    {t("click_season_from_list")}
                  </p>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="season-history-detail-card shadow-sm">
              <Card.Header
                style={{
                  background:
                    "linear-gradient(135deg, var(--history-teal, #0798ad) 0%, var(--history-teal-soft, #5fc9d3) 100%)",
                  color: "white",
                  padding: "12px 16px",
                }}
              >
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                  <div>
                    <h5
                      className="mb-1"
                      style={{ fontSize: "clamp(1rem, 4vw, 1.25rem)" }}
                    >
                      <i className="fas fa-calendar me-2" aria-hidden="true"></i>
                      {formatSeasonName(selectedSeason)}
                    </h5>
                    <small
                      style={{ fontSize: "clamp(0.75rem, 2.5vw, 0.875rem)" }}
                    >
                      {moment(selectedSeason.season_start).format(
                        "MMMM DD, YYYY"
                      )}
                      {selectedSeason.season_end
                        ? ` - ${moment(selectedSeason.season_end).format(
                            "MMMM DD, YYYY"
                          )}`
                        : ` - ${t("present")}`}
                    </small>
                  </div>
                  <Badge
                    bg={selectedSeason.is_active ? "success" : "secondary"}
                    style={{
                      fontSize: "clamp(0.75rem, 2.5vw, 0.875rem)",
                      padding: "6px 12px",
                    }}
                  >
                    {selectedSeason.is_active ? t("active") : t("completed")}
                  </Badge>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                <Tabs
                  activeKey={activeTab}
                  onSelect={(tab) => setActiveTab(tab)}
                  className="px-2 px-md-3 pt-2 pt-md-3"
                  fill
                  style={{ fontSize: "clamp(0.8rem, 3vw, 1rem)" }}
                >
                  {/* Overview Tab */}
                  <Tab eventKey="overview" title={t("overview")}>
                    <div className="p-2 p-md-4 ">
                      {/* Stats Cards */}
                      <Row className=" d-flex justify-content-around g-2 g-md-4 mb-3 mb-md-4">
                        <Col xs={6} lg={3} md={6}>
                          <Card
                            className="text-center h-100 border-0 shadow-sm"
                            style={{
                              background:
                                "linear-gradient(135deg, var(--sf-night-info-soft, #e3f2fd) 0%, var(--sf-night-info-soft, #bbdefb) 100%)",
                            }}
                          >
                            <Card.Body className="py-3 py-md-4 px-2">
                              <div className="mb-2">
                                <i className="fas fa-calendar-day fa-2x text-primary" aria-hidden="true"></i>
                              </div>
                              <h6
                                className="text-muted mb-2"
                                style={{
                                  fontSize: "clamp(0.7rem, 2.5vw, 0.875rem)",
                                }}
                              >
                                {t("duration")}
                              </h6>
                              <h3
                                className="mb-0"
                                style={{
                                  fontWeight: "700",
                                  color: "var(--history-teal, #0798ad)",
                                  fontSize: "clamp(1.2rem, 5vw, 1.75rem)",
                                }}
                              >
                                {getSeasonDuration(selectedSeason)}
                              </h3>
                              <small
                                className="text-muted"
                                style={{
                                  fontSize: "clamp(0.65rem, 2vw, 0.75rem)",
                                }}
                              >
                                {t("days")}
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col xs={6} lg={3} md={6}>
                          <Card
                            className="text-center h-100 border-0 shadow-sm"
                            style={{
                              background:
                                "linear-gradient(135deg, var(--sf-night-brand-soft, #e0f7fa) 0%, var(--sf-night-brand-soft, #b2ebf2) 100%)",
                            }}
                          >
                            <Card.Body className="py-3 py-md-4 px-2">
                              <div className="mb-2">
                                <i className="fas fa-tint fa-2x text-info" aria-hidden="true"></i>
                              </div>
                              <h6
                                className="text-muted mb-2"
                                style={{
                                  fontSize: "clamp(0.7rem, 2.5vw, 0.875rem)",
                                }}
                              >
                                {t("irrigations")}
                              </h6>
                              <h3
                                className="mb-0"
                                style={{
                                  fontWeight: "700",
                                  color: "var(--history-teal, #0798ad)",
                                  fontSize: "clamp(1.2rem, 5vw, 1.75rem)",
                                }}
                              >
                                {selectedSeason.irrigation_count.toFixed(3)}
                              </h3>
                               <small
                                className="text-muted"
                                style={{
                                  fontSize: "clamp(0.65rem, 2vw, 0.75rem)",
                                }}
                              >
                                {t("mm")}
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col xs={6} lg={3} md={6}>
                          <Card
                            className="text-center h-100 border-0 shadow-sm"
                            style={{
                              background:
                                "linear-gradient(135deg, var(--sf-night-warning-soft, #fff3e0) 0%, var(--sf-night-warning-soft, #ffe0b2) 100%)",
                            }}
                          >
                            <Card.Body className="py-3 py-md-4 px-2">
                              <div className="mb-2">
                                <i className="fas fa-list-alt fa-2x text-warning" aria-hidden="true"></i>
                              </div>
                              <h6
                                className="text-muted mb-2"
                                style={{
                                  fontSize: "clamp(0.7rem, 2.5vw, 0.875rem)",
                                }}
                              >
                                {t("events")}
                              </h6>
                              <h3
                                className="mb-0"
                                style={{
                                  fontWeight: "700",
                                  color: "var(--history-teal, #0798ad)",
                                  fontSize: "clamp(1.2rem, 5vw, 1.75rem)",
                                }}
                              >
                                {selectedSeason.event_count}
                              </h3>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      {/* Crop Details */}
                      {cropDetails.length > 0 && (
                        <Card className="border-0 shadow-sm">
                          <Card.Header
                            style={{
                              backgroundColor: "var(--history-mist, #effbfc)",
                              borderBottom: "2px solid var(--history-border, #e1eaec)",
                              fontWeight: "600",
                              padding: "12px 16px",
                              fontSize: "clamp(0.85rem, 3vw, 1rem)",
                            }}
                          >
                            <i className="fas fa-leaf me-2 text-success" aria-hidden="true"></i>
                            {t("crop_details")}
                          </Card.Header>
                          <Card.Body className="p-2 p-md-4">
                            {cropDetails.map((crop) => (
                              <div
                                key={crop.id}
                                className="mb-3 mb-md-4 pb-3 pb-md-4 border-bottom"
                              >
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3 gap-2">
                                  <h5
                                    className="mb-0"
                                    style={{
                                      color: "var(--history-teal, #0798ad)",
                                      fontSize: "clamp(1rem, 4vw, 1.25rem)",
                                    }}
                                  >
                                    <i className="fas fa-seedling me-2" aria-hidden="true"></i>
                                    {crop.name}
                                    {crop.variety &&
                                      crop.variety !== "No variety" && (
                                        <span
                                          className="text-muted ms-2 d-block d-md-inline"
                                          style={{
                                            fontSize:
                                              "clamp(0.8rem, 3vw, 0.9rem)",
                                            fontWeight: "normal",
                                          }}
                                        >
                                          ({crop.variety})
                                        </span>
                                      )}
                                  </h5>
                                  {crop.isKcModified && (
                                    <Badge
                                      bg="info"
                                      title={t("kc_modified")}
                                      style={{
                                        fontSize:
                                          "clamp(0.7rem, 2.5vw, 0.75rem)",
                                      }}
                                    >
                                      <i className="fas fa-edit me-1" aria-hidden="true"></i>
                                      {t("kc_edited")}
                                    </Badge>
                                  )}
                                </div>

                                <Row className="g-3 g-md-4">
                                  <Col md={6} xs={12}>
                                    <h6
                                      className="mb-3"
                                      style={{
                                        color: "var(--history-teal, #0798ad)",
                                        fontSize: "clamp(0.85rem, 3vw, 1rem)",
                                      }}
                                    >
                                      <i className="fas fa-info-circle me-2" aria-hidden="true"></i>
                                      {t("basic_info")}
                                    </h6>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "140px" }}
                                      >
                                        {t("planting_date")}:
                                      </span>
                                      <strong>
                                        {crop.plantingDate
                                          ? moment(crop.plantingDate).format(
                                              "MMM DD, YYYY"
                                            )
                                          : "N/A"}
                                      </strong>
                                    </div>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "140px" }}
                                      >
                                        {t("wetted_surface")}:
                                      </span>
                                      <strong>{crop.surface} m²</strong>
                                    </div>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "140px" }}
                                      >
                                        {t("total_days")}:
                                      </span>
                                      <strong>
                                        {crop.days} {t("days")}
                                      </strong>
                                    </div>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "140px" }}
                                      >
                                        {t("root_depth")}:
                                      </span>
                                      <strong>{crop.rootDepth} m</strong>
                                    </div>
                                  </Col>

                                  <Col md={6} xs={12}>
                                    <h6
                                      className="mb-3"
                                      style={{
                                        color: "var(--history-teal, #0798ad)",
                                        fontSize: "clamp(0.85rem, 3vw, 1rem)",
                                      }}
                                    >
                                      <i className="fas fa-chart-line me-2" aria-hidden="true"></i>
                                      {t("growth_stages")}
                                    </h6>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "120px" }}
                                      >
                                        {t("initial")}:
                                      </span>
                                      <strong>{crop.init} days</strong>
                                    </div>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "120px" }}
                                      >
                                        {t("development")}:
                                      </span>
                                      <strong>{crop.dev} days</strong>
                                    </div>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "120px" }}
                                      >
                                        {t("mid_season")}:
                                      </span>
                                      <strong>{crop.mid} days</strong>
                                    </div>
                                    <div
                                      className="mb-2"
                                      style={{
                                        fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                                      }}
                                    >
                                      <span
                                        className="text-muted d-inline-block"
                                        style={{ width: "120px" }}
                                      >
                                        {t("late_season")}:
                                      </span>
                                      <strong>{crop.late} days</strong>
                                    </div>

                                    <h6
                                      className="mb-3 mt-3 mt-md-4"
                                      style={{
                                        color: "var(--history-teal, #0798ad)",
                                        fontSize: "clamp(0.85rem, 3vw, 1rem)",
                                      }}
                                    >
                                      <i className="fas fa-tint me-2" aria-hidden="true"></i>
                                      {t("kc_values")}
                                    </h6>
                                    <div className="d-flex flex-wrap gap-3 gap-md-4">
                                      <div>
                                        <small
                                          className="text-muted d-block"
                                          style={{
                                            fontSize:
                                              "clamp(0.7rem, 2.5vw, 0.75rem)",
                                          }}
                                        >
                                          {t("kc_initial")}
                                        </small>
                                        <strong
                                          style={{
                                            fontSize:
                                              "clamp(0.9rem, 3.5vw, 1rem)",
                                          }}
                                        >
                                          {crop.kc_init.toFixed(2)}
                                        </strong>
                                      </div>
                                      <div>
                                        <small
                                          className="text-muted d-block"
                                          style={{
                                            fontSize:
                                              "clamp(0.7rem, 2.5vw, 0.75rem)",
                                          }}
                                        >
                                          {t("kc_mid")}
                                        </small>
                                        <strong
                                          style={{
                                            fontSize:
                                              "clamp(0.9rem, 3.5vw, 1rem)",
                                          }}
                                        >
                                          {crop.kc_mid.toFixed(2)}
                                        </strong>
                                      </div>
                                      <div>
                                        <small
                                          className="text-muted d-block"
                                          style={{
                                            fontSize:
                                              "clamp(0.7rem, 2.5vw, 0.75rem)",
                                          }}
                                        >
                                          {t("kc_late")}
                                        </small>
                                        <strong
                                          style={{
                                            fontSize:
                                              "clamp(0.9rem, 3.5vw, 1rem)",
                                          }}
                                        >
                                          {crop.kc_late.toFixed(2)}
                                        </strong>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </Card.Body>
                        </Card>
                      )}
                    </div>
                  </Tab>

                  {/* Calculations Tab */}
                  <Tab eventKey="calculations" title={t("calculations")}>
                    <div className="p-2 p-md-4">
                      <Card className="border-0 shadow-sm">
                        <Card.Header
                          style={{
                            backgroundColor: "var(--history-mist, #effbfc)",
                            borderBottom: "2px solid var(--history-border, #e1eaec)",
                            fontWeight: "600",
                            padding: "12px 16px",
                            fontSize: "clamp(0.85rem, 3vw, 1rem)",
                          }}
                        >
                          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                            <span>
                              <i className="fas fa-calculator me-2 text-info" aria-hidden="true"></i>
                              {t("water_balance_calculations")}
                            </span>
                            {weeklyData.length > 0 && (
                              <div className="d-flex flex-wrap gap-2">
                                <Badge
                                  bg="primary"
                                  style={{
                                    fontSize: "clamp(0.7rem, 2.5vw, 0.875rem)",
                                  }}
                                >
                                  {weeklyData.length} {t("20_weeks")}
                                </Badge>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/calculations/${selectedSeason.id}`
                                    )
                                  }
                                  style={{
                                    fontSize: "clamp(0.7rem, 2.5vw, 0.875rem)",
                                  }}
                                >
                                  <i className="fas fa-external-link-alt me-1" aria-hidden="true"></i>
                                  <span className="d-none d-md-inline">
                                    {t("view_detailed_analysis")}
                                  </span>
                                  <span className="d-inline d-md-none">
                                    {t("details")}
                                  </span>
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card.Header>
                        <Card.Body className="p-2 p-md-4">
                          {loadingCalculations ? (
                            <div className="text-center py-4 py-md-5">
                              <div
                                className="spinner-border text-info"
                                role="status"
                                style={{ width: "2.5rem", height: "2.5rem" }}
                              ></div>
                              <p
                                className="mt-3 text-muted"
                                style={{
                                  fontSize: "clamp(0.85rem, 3vw, 1rem)",
                                }}
                              >
                                {t("loading_calculations")}
                              </p>
                            </div>
                          ) : weeklyData.length === 0 ? (
                            <div className="text-center py-4 py-md-5 text-muted">
                              <i className="fas fa-calculator fa-2x fa-md-3x mb-3 opacity-50" aria-hidden="true"></i>
                              <p
                                style={{
                                  fontSize: "clamp(0.85rem, 3vw, 1rem)",
                                }}
                              >
                                {t("no_calculations_found")}
                              </p>
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table
                                className="table table-hover"
                                style={{
                                  fontSize: "clamp(0.75rem, 2.5vw, 0.875rem)",
                                }}
                              >
                                <thead className="table-light">
                                  <tr>
                                    <th
                                      style={{
                                        padding: "12px 8px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {t("week")}
                                    </th>
                                    <th
                                      className="text-center"
                                      style={{ padding: "12px 8px" }}
                                    >
                                      {t("days")}
                                    </th>
                                    <th
                                      className="text-center d-none d-md-table-cell"
                                      style={{
                                        padding: "12px 8px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {t("avg_eto")}
                                    </th>
                                    <th
                                      className="text-center d-none d-lg-table-cell"
                                      style={{ padding: "12px 8px" }}
                                    >
                                      {t("avg_kc")}
                                    </th>
                                    <th
                                      className="text-center"
                                      style={{
                                        padding: "12px 8px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {t("avg_etc")}
                                    </th>
                                    <th
                                      className="text-center d-none d-md-table-cell"
                                      style={{ padding: "12px 8px" }}
                                    >
                                      {t("total_rain")}
                                    </th>
                                    <th
                                      className="text-center"
                                      style={{ padding: "12px 8px" }}
                                    >
                                      {t("total_irrigation")}
                                    </th>
                                    <th
                                      className="text-center d-none d-lg-table-cell"
                                      style={{ padding: "12px 8px" }}
                                    >
                                      {t("final_bilan")}
                                    </th>
                                    <th
                                      className="text-center"
                                      style={{ padding: "12px 8px" }}
                                    >
                                      {t("actions")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {weeklyData.map((week, idx) => {
                                    const averages = getWeekAverages(
                                      week.daily_calculations
                                    );
                                    const hasIrrigation = hasWeekIrrigation(
                                      week.daily_calculations
                                    );

                                    return (
                                      <tr key={week.id}>
                                        <td style={{ padding: "12px 8px" }}>
                                          <div>
                                            <strong>{t("20_weeks")} {idx + 1}</strong>
                                            <div
                                              style={{
                                                fontSize: "0.7rem",
                                                color: "var(--history-muted, #71848a)",
                                              }}
                                            >
                                              {moment(week.week_start).format(
                                                "MMM DD"
                                              )}{" "}
                                              -{" "}
                                              {moment(week.week_end).format(
                                                "MMM DD"
                                              )}
                                            </div>
                                          </div>
                                        </td>
                                        <td
                                          className="text-center"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          <Badge
                                            bg="secondary"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {week.summary?.total_days || 0}
                                          </Badge>
                                        </td>
                                        <td
                                          className="text-center d-none d-md-table-cell"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          <Badge
                                            bg="outline-primary"
                                            text="dark"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {safeToFixed(averages.avgET0)}
                                          </Badge>
                                        </td>
                                        <td
                                          className="text-center d-none d-lg-table-cell"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          <Badge
                                            bg="outline-secondary"
                                            text="dark"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {safeToFixed(averages.avgKc)}
                                          </Badge>
                                        </td>
                                        <td
                                          className="text-center"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          <Badge
                                            bg="primary"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {safeToFixed(averages.avgEtc)}
                                          </Badge>
                                        </td>
                                        <td
                                          className="text-center d-none d-md-table-cell"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          <Badge
                                            bg="info"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {safeToFixed(
                                              week.summary?.total_rain || 0
                                            )}
                                          </Badge>
                                        </td>
                                        <td
                                          className="text-center"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          {hasIrrigation ? (
                                            <Badge
                                              bg="success"
                                              style={{ fontSize: "0.7rem" }}
                                            >
                                              {safeToFixed(
                                                week.summary
                                                  ?.total_irrigation || 0
                                              )}
                                            </Badge>
                                          ) : (
                                            <Badge
                                              bg="secondary"
                                              style={{ fontSize: "0.7rem" }}
                                            >
                                              0.00
                                            </Badge>
                                          )}
                                        </td>
                                        <td
                                          className="text-center d-none d-lg-table-cell"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          <Badge
                                            bg={
                                              averages.lastBilan > 70
                                                ? "success"
                                                : averages.lastBilan > 50
                                                ? "warning"
                                                : "danger"
                                            }
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {safeToFixed(averages.lastBilan)}
                                          </Badge>
                                        </td>
                                        <td
                                          className="text-center"
                                          style={{ padding: "12px 8px" }}
                                        >
                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() =>
                                              navigate(
                                                `/calculations/${
                                                  selectedSeason.id
                                                }?week=${idx + 1}`
                                              )
                                            }
                                            style={{
                                              fontSize: "0.7rem",
                                              padding: "4px 8px",
                                            }}
                                          >
                                            <i className="fas fa-chart-bar me-1" aria-hidden="true"></i>
                                            <span className="d-none d-sm-inline">
                                              {t("details")}
                                            </span>
                                            <i className="fas fa-eye d-inline d-sm-none" aria-hidden="true"></i>
                                          </Button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default FieldHistory;
